import {observer} from 'mobx-react';
import React from 'react';

import {X2AgeCategory} from '../../../domain';
import {DisplayError, Link, PageHeading, PromiseMessage} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {AddAgeCategoryControls} from './parts/AddAgeCategoryControls';
import {AddAgeCategoryForm} from './parts/AddAgeCategoryForm';
import {DeleteAgeCategoryControls} from './parts/DeleteAgeCategoryControls';
import {EditAgeCategoryControls} from './parts/EditAgeCategoryControls';
import {EditAgeCategoryForm} from './parts/EditAgeCategoryForm';
import {MoveAgeCategoryControls} from './parts/MoveAgeCategoryControls';
import {MoveAgeCategoryForm} from './parts/MoveAgeCategoryForm';
import {ReorderAgeCategoriesControls} from './parts/ReorderAgeCategoriesControls';
import {X1LanguageBreadcrumbs} from './X1LanguageBreadcrumbs';
import {X1LanguageStore} from './X1LanguageStore';
import {userStatusService} from '../../../domain'

function AgeCategoryViewNoActions(ageCategory: X2AgeCategory): React.ReactNode {
  return (
    <div className="row" key={'ageCategory-' + ageCategory.Id}>
      <div className="col">
        <div className="text-box">
          {ageCategory.Name}
        </div>
      </div>
    </div>
  );
}

function* ViewModeItems(store: X1LanguageStore): ReactNodeGenerator {
  const ageCategories = store.loader.ageCategories;
  if (ageCategories.length === 0)
    yield (
      <div className="row justify-content-center" key="no-age-categories">
        <div className="col-auto">
          <div className="alert alert-info">
            No Age categories
          </div>
        </div>
      </div>);

  for (const ageCategory of ageCategories) {
    yield (
      <div className="row" key={'ageCategory-' + ageCategory.Id}>
        <div className="col">
          <div className="text-box">
            {ageCategory.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <Link className="btn btn-primary" to={routes.x2ageCategory(ageCategory.Id)}> View </Link>
          {
            userStatusService.status.roles.ContentManager &&
            <>
              <button type="button" className="btn btn-primary"
                      onClick={() => store.beginEdit(ageCategory.Id)}> Edit
              </button>
              <button type="button" className="btn btn-primary"
                      onClick={() => store.beginMove(ageCategory.Id)}> Move
              </button>
              <button type="button" className="btn btn-danger"
                      onClick={() => store.beginDelete(ageCategory.Id)}> Delete
              </button>
            </>
          }
         
        </div>
      </div>);
  }
}

function* AddModeItems(store: X1LanguageStore): ReactNodeGenerator {
  const ageCategories = store.loader.ageCategories;

  for (const ageCategory of ageCategories)
    yield AgeCategoryViewNoActions(ageCategory);

  yield (<AddAgeCategoryForm key="add-age-category-form"
                             store={store}/>);

  yield (<AddAgeCategoryControls key="add-age-category-controls"
                                 store={store}/>);
}

function* EditModeItems(store: X1LanguageStore): ReactNodeGenerator {
  const ageCategories = store.loader.ageCategories;
  const editAgeCategoryId = store.editAgeCategory?.ageCategory.Id;

  for (const ageCategory of ageCategories) {
    if (ageCategory.Id !== editAgeCategoryId) {
      yield AgeCategoryViewNoActions(ageCategory);
      continue;
    }

    yield (<EditAgeCategoryForm key="edit-age-category-form"
                                store={store}/>);

    yield (<EditAgeCategoryControls key="edit-age-category-controls"
                                    store={store}/>);
  }
}

function* MoveModeItems(store: X1LanguageStore): ReactNodeGenerator {
  const ageCategories = store.loader.ageCategories;
  const moveAgeCategoryId = store.move!.ageCategory.Id;
  for (const ageCategory of ageCategories) {
    yield AgeCategoryViewNoActions(ageCategory);

    if (ageCategory.Id !== moveAgeCategoryId)
      continue;

    yield (<MoveAgeCategoryForm key="move-age-category-form" store={store}/>);

    yield (<MoveAgeCategoryControls key="move-age-category-controls" store={store}/>);
  }
}

function* DeleteModeItems(store: X1LanguageStore): ReactNodeGenerator {
  const ageCategories = store.loader.ageCategories;
  const deleteAgeCategoryId = store.deleteAgeCategory!.ageCategoryId;

  for (let ageCategory of ageCategories) {
    yield AgeCategoryViewNoActions(ageCategory);

    if (ageCategory.Id !== deleteAgeCategoryId) {
      continue;
    }

    yield (<DeleteAgeCategoryControls key="delete-age-category-controls"
                                      store={store}/>);
  }
}

function* ReorderModeItems(store: X1LanguageStore): ReactNodeGenerator {
  const ageCategoryIds = store.reorder!.ageCategoryIds;
  const ageCategoryById = store.loader.ageCategoryById;

  for (let index = 0; index < ageCategoryIds.length; index++) {
    let ageCategoryId = ageCategoryIds[index];
    const ageCategory = ageCategoryById[ageCategoryId];
    const first = index === 0;
    const last = index === ageCategoryIds.length - 1;

    yield (
      <div className="row" key={'ageCategory-' + ageCategory.Id}>
        <div className="col">
          <div className="text-box">
            {ageCategory.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderUp(index)}
                  disabled={first}>
            <span className="fas fa-arrow-up"/>
          </button>
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderDown(index)}
                  disabled={last}>
            <span className="fas fa-arrow-down"/>
          </button>
        </div>
      </div>);
  }

  yield (<ReorderAgeCategoriesControls key="reorder-age-categories-controls"
                                       store={store}/>);
}

function* cardBodyRenderer(store: X1LanguageStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'View':
      yield* ViewModeItems(store);
      break;
    case 'Edit':
      yield* EditModeItems(store);
      break;
    case 'Add':
      yield* AddModeItems(store);
      break;
    case 'Delete':
      yield* DeleteModeItems(store);
      break;
    case 'ReOrder':
      yield* ReorderModeItems(store);
      break;
    case 'Move':
      yield* MoveModeItems(store);
      break;
  }
}

function* sceneRenderer(store: X1LanguageStore): ReactNodeGenerator {
  const loaderState = store.loader.state;
  const isPending = loaderState?.isPending === true;
  const isFailure = loaderState?.isRejected === true;
  const isSuccess = loaderState?.isResolved === true;
  const language = store.loader.language;

  if (isPending || isFailure) {
    yield (<PageHeading key="heading">Language </PageHeading>);

    yield (
      <PromiseMessage key="lookup-loader"
                      label="Loading Age Categories"
                      state={loaderState} align="right"/>);

    if (isFailure)
      yield (
        <div className="row justify-content-center" key="lookup-error">
          <div className="col-auto">
            <button type="button" className="btn btn-primary"
                    onClick={store.loader.load}>
              Retry
            </button>
          </div>
        </div>
      );
    return;
  }

  if (!isSuccess)
    return;

  if (language === null) {
    yield (
      <DisplayError key="language-not-found">
        Language with Id {store.languageId} not found
      </DisplayError>);
    return;
  }

  yield (<X1LanguageBreadcrumbs key="language-component-breadcrumbs"
                                language={language!}/>);

  yield (<PageHeading key="heading"> Language {language!.Name} </PageHeading>);

  const ageCategories = store.loader.ageCategories;

  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col"> Age categories</div>
              {
                store.mode === 'View' &&
                <div className="col-auto no-wrap">
                  {
                    userStatusService.status.roles.ContentManager &&
                    <>
                      <button type="button" className="btn btn-primary"
                              onClick={store.beginAdd}> Add
                      </button>
                      <button type="button" className="btn btn-primary"
                              disabled={ageCategories.length < 2}
                              onClick={store.beginReorder}> Reorder
                      </button>
                    </>
                  }
                </div>
              }
            </div>
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(store))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  id: number;
}

@observer
export class X1LanguageComponent extends React.Component<Props> {
  readonly store: X1LanguageStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new X1LanguageStore(props.id);
  }

  componentDidMount() {
    this.store.loader.load();
  }

  render() {
    return Array.from(sceneRenderer(this.store));
  }

  componentWillUnmount(): void {
    this.store?.dispose();
  }
}
